import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  SELECTABLE_ALTERNATIVE_MODES,
  SelectableAlternativeMode,
} from 'src/app/impact/models/reduction-tip';
import { TransportAlternativeOptions } from 'src/app/shared/model/reduction-action';
import { ReductionActionsService } from 'src/app/shared/services/reduction-actions.service';

/** Mirrors the API validator; the API stays the judge. */
const BOUNDS = {
  maxFlightDistanceKm: { min: 100, max: 5000 },
  maxDurationHours: { min: 1, max: 48 },
  maxChanges: { min: 0, max: 5 },
} as const;

/**
 * What the company accepts as a ground alternative to a flight: how long a
 * flight still gets compared, and which itineraries are worth proposing.
 */
@Component({
  selector: 'app-transport-alternative-options',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
  ],
  templateUrl: './transport-alternative-options.component.html',
})
export class TransportAlternativeOptionsComponent {
  private reductionActionsService = inject(ReductionActionsService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly options = input.required<TransportAlternativeOptions>();
  readonly saved = output<TransportAlternativeOptions>();

  readonly modes = SELECTABLE_ALTERNATIVE_MODES;
  readonly bounds = BOUNDS;

  maxFlightDistanceKm = linkedSignal(() => this.options().maxFlightDistanceKm);
  maxDurationHours = linkedSignal(() => this.options().maxDurationHours);
  maxChanges = linkedSignal(() => this.options().maxChanges);
  allowedModes = linkedSignal<SelectableAlternativeMode[]>(() => [
    ...this.options().allowedModes,
  ]);

  isSaving = signal(false);

  /** Re-emits once a language's translations are loaded, so the summary follows a switch. */
  private translation = toSignal(this.translocoService.selectTranslation());

  draft = computed<TransportAlternativeOptions>(() => ({
    maxFlightDistanceKm: this.maxFlightDistanceKm() ?? null,
    maxDurationHours: this.maxDurationHours() ?? null,
    maxChanges: this.maxChanges() ?? null,
    allowedModes: this.allowedModes(),
  }));

  isDirty = computed(() => {
    const saved = this.options();
    const draft = this.draft();
    return (
      saved.maxFlightDistanceKm !== draft.maxFlightDistanceKm ||
      saved.maxDurationHours !== draft.maxDurationHours ||
      saved.maxChanges !== draft.maxChanges ||
      saved.allowedModes.length !== draft.allowedModes.length ||
      saved.allowedModes.some((mode) => !draft.allowedModes.includes(mode))
    );
  });

  /**
   * The draft read back as one sentence, so the reader sees what the settings
   * do together rather than having to infer it from three separate fields.
   */
  summary = computed<string | null>(() => {
    this.translation();
    const { maxFlightDistanceKm, maxDurationHours, maxChanges, allowedModes } =
      this.draft();
    if (allowedModes.length === 0) return null;

    const key = (k: string) =>
      `objectives.transportAlternativeOptions.summary.${k}`;
    const lang = this.translocoService.getActiveLang();
    const modes = new Intl.ListFormat(lang, { type: 'disjunction' }).format(
      // Keep catalogue order whatever order the boxes were ticked in.
      this.modes
        .filter((mode) => allowedModes.includes(mode))
        .map((mode) => this.translocoService.translate(key(`modes.${mode}`))),
    );

    const conditions: string[] = [];
    if (maxDurationHours !== null) {
      conditions.push(
        this.translocoService.translate(key('maxDuration'), {
          hours: maxDurationHours,
        }),
      );
    }
    if (maxChanges !== null) {
      const changesKey =
        maxChanges === 0
          ? 'direct'
          : maxChanges === 1
            ? 'maxChangesOne'
            : 'maxChanges';
      conditions.push(
        this.translocoService.translate(key(changesKey), {
          changes: maxChanges,
        }),
      );
    }

    return this.translocoService.translate(
      key(maxFlightDistanceKm === null ? 'sentenceAllFlights' : 'sentence'),
      {
        distance: maxFlightDistanceKm,
        modes,
        conditions: conditions.length
          ? `, ${new Intl.ListFormat(lang, { type: 'conjunction' }).format(conditions)}`
          : '',
      },
    );
  });

  canSave = computed(
    () => this.isDirty() && !this.isSaving() && this.allowedModes().length > 0,
  );

  isAllowed(mode: SelectableAlternativeMode): boolean {
    return this.allowedModes().includes(mode);
  }

  toggleMode(mode: SelectableAlternativeMode): void {
    this.allowedModes.update((modes) =>
      modes.includes(mode) ? modes.filter((m) => m !== mode) : [...modes, mode],
    );
  }

  save(): void {
    if (!this.canSave()) return;

    this.isSaving.set(true);
    this.reductionActionsService
      .updateTransportAlternativeOptions(this.draft())
      .subscribe({
        next: (options) => {
          this.isSaving.set(false);
          this.saved.emit(options);
        },
        error: () => {
          this.isSaving.set(false);
          this.notificationService.error(
            this.translocoService.translate('objectives.sdgSaveError'),
          );
        },
      });
  }
}
